/**
 * Which customers are waiting on us (ADR 0227, issue #476). What counts as
 * our reply to whom: ADR 0269.
 *
 * The Messages section used to list every customer message on its own, sorted
 * by what Jev read in it on the day it arrived (ADR 0222). Nothing ever left
 * the list, so a message answered a week ago sat at the top as "needs
 * attention today". This groups messages by customer and keeps a customer
 * only while they spoke last: our newest text or email to them, on any
 * channel, older than their newest message, and nobody has marked it handled.
 *
 * Jev's answers still order the list and say what a customer wants; they
 * never decide whether a customer is listed. A customer whose unanswered
 * messages all read as needing nothing is listed apart, not hidden.
 */

import {
  CHECK_REASONS,
  messagePriority,
  type Priority,
  type Signals,
} from "./messagePriority.js";
import { phoneMatchKey } from "./phoneContacts.js";

/** One message either way, as the inbox needs it. */
export interface InboxMessage {
  /** Inbound: the reading's id, which is what a "handled" mark refers to. */
  id: string;
  direction: "inbound" | "outbound";
  channel: "email" | "text";
  sent_at: string;
  /** The customer, when known: a text's contact, or an email's job's contact. */
  contact_id: string | null;
  /** An email's thread: our reply in it answers the customer who wrote in it. */
  thread_id: string | null;
  job_id: string | null;
  /** Who an email is from, as its header says. */
  from: string | null;
  /** An inbound email's sender address, which groups a customer with no contact. */
  from_address?: string | null;
  /**
   * An outbound email's recipients, To and Cc. When known, it answers only
   * the customers it went to: an email on a job's thread to the adjuster or a
   * supplier answers nobody (issue #499).
   */
  to_addresses?: readonly string[] | null;
  /** A text's customer number, which groups a customer with no contact. */
  phone?: string | null;
  subject: string | null;
  text: string;
  /** Inbound only; null until rank-inbound has asked about it. */
  signals: Signals | null;
}

/**
 * A customer is late once they have waited this long: three days, Jonathon,
 * 2026-09-30 (ADR 0259), where it had been one.
 */
export const INBOX_LATE_HOURS = 72;

/** What a waiting customer shows in the list. */
export interface InboxCustomer {
  key: string;
  contact_id: string | null;
  /**
   * The job their conversation opens on, which is where a reply from the
   * inbox lands: the job their newest message is about, else their newest
   * job (`conversationJob`). Null when neither is among the jobs given.
   * With no jobs given at all, the job the newest message is about.
   */
  job_id: string | null;
  from: string | null;
  /** The channel of their newest message, which is where to answer them. */
  channel: "email" | "text";
  /** Their messages since our last reply, oldest first, not marked handled. */
  unanswered: InboxMessage[];
  /** The oldest of those: how long they have waited. */
  waiting_since: string;
  /** The newest of those, its text with attachment placeholders taken out. */
  latest: {
    text: string;
    subject: string | null;
    attachments: number;
    sent_at: string;
  };
  /** Jev said one of them needs attention today. */
  same_day: boolean;
  /** One short reason, the strongest check among them; null when unsure. */
  reason: string | null;
}

export interface Inbox {
  /** Same-day asks first, then whoever has waited longest. */
  waiting: InboxCustomer[];
  /** Everything unanswered reads as needing nothing. Newest first. */
  no_reply_needed: InboxCustomer[];
  /** Customers who wrote in the window and have had an answer. */
  replied: number;
}

/**
 * Which customer a message belongs to. A contact where one is known; else a
 * text's customer number, the way an email falls back to its sender; else an
 * email's sender address, which our reply in the same thread inherits
 * (`threadSender`); else the thread; else the message on its own.
 */
export function inboxCustomerKey(
  message: InboxMessage,
  threadSender: ReadonlyMap<string, string> = new Map(),
): string {
  if (message.contact_id) return `contact:${message.contact_id}`;
  const phone = textPhone(message);
  if (phone) return `phone:${phone}`;
  const address =
    normalisedAddress(message.from_address) ??
    (message.thread_id ? threadSender.get(message.thread_id) : undefined);
  if (address) return `email:${address}`;
  if (message.thread_id) return `thread:${message.thread_id}`;
  return `message:${message.id}`;
}

/** An outbound email's recipients, when known. */
function recipients(message: InboxMessage): string[] | null {
  if (message.direction !== "outbound" || message.channel !== "email")
    return null;
  if (!message.to_addresses) return null;
  return message.to_addresses
    .map(normalisedAddress)
    .filter((a): a is string => a !== null);
}

/** A text's number as its last ten digits, so two spellings of it match. */
function textPhone(message: InboxMessage): string | null {
  return message.channel === "text" ? phoneMatchKey(message.phone) : null;
}

function normalisedAddress(address: string | null | undefined): string | null {
  const trimmed = address?.trim().toLowerCase();
  return trimmed ? trimmed : null;
}

/**
 * A phone writes U+FFFC where a picture sat in an MMS. It is not text, and a
 * screen draws it as a box ("OBJ"), so it is counted and taken out.
 */
export function messageAttachments(text: string): {
  text: string;
  attachments: number;
} {
  const attachments = (text.match(/\uFFFC/g) ?? []).length;
  return {
    text: attachments ? text.replace(/\uFFFC/g, "").trim() : text,
    attachments,
  };
}

/** "Waiting 35 min", "Waiting 4 hrs", "Waiting 1 day", "Waiting 6 days". */
export function waitingLabel(since: string, now: Date): string {
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - Date.parse(since)) / 60_000),
  );
  if (minutes < 60) return `Waiting ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Waiting ${hours} ${hours === 1 ? "hr" : "hrs"}`;
  const days = Math.floor(hours / 24);
  return `Waiting ${days} ${days === 1 ? "day" : "days"}`;
}

/** At or past `INBOX_LATE_HOURS`. */
export function waitingIsLate(since: string, now: Date): boolean {
  return now.getTime() - Date.parse(since) >= INBOX_LATE_HOURS * 3_600_000;
}

/** A job as the inbox needs it: whose it is, and how new. */
export interface InboxJob {
  id: string;
  contact_id: string | null;
  created_at: string;
}

/**
 * The job a customer's conversation opens on: the one they wrote about, when
 * it is among `jobs`; else the contact's newest job by `created_at` (ties by
 * id, so the answer never depends on the order `jobs` came in); else none.
 */
export function conversationJob(
  aboutJobId: string | null,
  contactId: string | null,
  jobs: readonly InboxJob[],
): string | null {
  if (aboutJobId && jobs.some((job) => job.id === aboutJobId))
    return aboutJobId;
  if (!contactId) return null;
  let newest: InboxJob | null = null;
  for (const job of jobs) {
    if (job.contact_id !== contactId) continue;
    if (
      !newest ||
      job.created_at > newest.created_at ||
      (job.created_at === newest.created_at && job.id > newest.id)
    )
      newest = job;
  }
  return newest?.id ?? null;
}

/**
 * Each message's customer, where it can be told: an email through its job,
 * unless it is ours and we know who it went to (`addressedKey` decides
 * those); a text with no contact through another text on the same number.
 */
function resolveContacts(
  messages: readonly InboxMessage[],
  contactOfJob: ReadonlyMap<string, string | null>,
): InboxMessage[] {
  const byJob = messages.map((original) =>
    original.contact_id || !original.job_id || recipients(original)
      ? original
      : { ...original, contact_id: contactOfJob.get(original.job_id) ?? null },
  );
  /** A number one text ties to a contact, so their other texts join them. */
  const contactOfPhone = new Map<string, string>();
  for (const message of byJob) {
    const phone = textPhone(message);
    if (phone && message.contact_id && !contactOfPhone.has(phone))
      contactOfPhone.set(phone, message.contact_id);
  }
  return byJob.map((original) => {
    const phone = original.contact_id ? null : textPhone(original);
    const contact = phone ? contactOfPhone.get(phone) : undefined;
    return contact ? { ...original, contact_id: contact } : original;
  });
}

/** What the window tells us about who writes from which address. */
interface KnownAddresses {
  /** Who wrote in each thread, so our reply there lands on their row. */
  threadSender: Map<string, string>;
  /**
   * The row our reply to that sender lands on: their own row, never the
   * job's customer's, which their message may be filed under.
   */
  threadKey: Map<string, string>;
  /**
   * Every address a customer wrote from, and the row their messages are on,
   * so our reply to that address lands on the same row (issue #818).
   */
  senders: Map<string, string>;
  /** The addresses each contact is known by: the email on their contact. */
  addressesOf: Map<string, Set<string>>;
  /** Whose each address is, the first contact known by it. */
  contactOfAddress: Map<string, string>;
}

function knownAddresses(
  messages: readonly InboxMessage[],
  addressesOfContact: ReadonlyMap<string, readonly string[]>,
): KnownAddresses {
  const known: KnownAddresses = {
    threadSender: new Map(),
    threadKey: new Map(),
    senders: new Map(),
    addressesOf: new Map(),
    contactOfAddress: new Map(),
  };
  const learn = (contact: string, address: string | null) => {
    if (!address) return;
    const set = known.addressesOf.get(contact) ?? new Set<string>();
    known.addressesOf.set(contact, set.add(address));
    if (!known.contactOfAddress.has(address))
      known.contactOfAddress.set(address, contact);
  };
  for (const [contact, list] of addressesOfContact)
    for (const address of list) learn(contact, normalisedAddress(address));
  for (const message of messages) {
    const address = normalisedAddress(message.from_address);
    if (message.direction !== "inbound" || !address) continue;
    // An email on a job is filed under the job's customer, not its own row.
    known.senders.set(
      address,
      message.job_id ? `job:${message.job_id}` : inboxCustomerKey(message),
    );
    if (!message.thread_id) continue;
    known.threadSender.set(message.thread_id, address);
    // Keyed as the message itself names it, not by the job it is filed
    // under: the adjuster or a supplier writes on the job's thread too, so
    // our reply to them must not answer the job's customer (issue #807). An
    // address a job-filed email came from is never learned as the
    // customer's for the same reason (issue #808).
    known.threadKey.set(message.thread_id, inboxCustomerKey(message));
  }
  return known;
}

/**
 * Whom our email answers when we know who it went to: the job's customer,
 * whoever wrote in its thread, another contact, or a sender with no contact,
 * if it went to them; a thread nobody's address is known in, as before;
 * otherwise nobody (issue #499).
 */
function addressedKey(
  message: InboxMessage,
  to: readonly string[],
  known: KnownAddresses,
  contactOfJob: ReadonlyMap<string, string | null>,
): string {
  const ofJob = message.job_id ? contactOfJob.get(message.job_id) : null;
  if (ofJob && to.some((a) => known.addressesOf.get(ofJob)?.has(a)))
    return `contact:${ofJob}`;
  // Whoever wrote in this thread, by their own address unless their message
  // named its contact; on a job's thread that is not the customer, who is
  // answered only at the email on their contact, above.
  const thread = message.thread_id;
  const threadSender = thread ? known.threadSender.get(thread) : undefined;
  const threadKey = thread ? known.threadKey.get(thread) : undefined;
  if (threadSender && threadKey && to.includes(threadSender)) return threadKey;
  // Someone whose messages sit on their own address's row: an email with no
  // job is keyed by its address even when that address is on a contact, so a
  // reply keyed by the contact would leave them waiting (issue #818). Only
  // their own row: one filed under a job's customer is not answered by a
  // fresh email, since the adjuster writes on that thread too (issue #808).
  const own = to.find((a) => known.senders.get(a) === `email:${a}`);
  if (own) return `email:${own}`;
  const contact = to
    .map((a) => known.contactOfAddress.get(a))
    .find((c) => c !== undefined);
  if (contact) return `contact:${contact}`;
  const sender = to.find((a) => known.senders.has(a));
  if (sender) return `email:${sender}`;
  if (message.thread_id && !known.threadSender.has(message.thread_id))
    return `thread:${message.thread_id}`;
  return `message:${message.id}`;
}

/**
 * The inbox, from every message either way in the window and the inbound
 * messages somebody marked handled.
 */
export function buildInbox(
  messages: readonly InboxMessage[],
  handled: ReadonlySet<string> = new Set(),
  /**
   * Each job's customer. An email knows its job, not its contact; without
   * this, a customer who emails and is answered by text would stay waiting.
   */
  contactOfJob: ReadonlyMap<string, string | null> = new Map(),
  /**
   * The addresses each contact is known by: the email on their contact. Our
   * email answers a contact only when it went to one of these (issue #499),
   * or, for a sender with no contact, to the address they wrote from.
   */
  addressesOfContact: ReadonlyMap<string, readonly string[]> = new Map(),
  /**
   * Every job the caller can open: which one each conversation opens on.
   * Without it, each conversation opens on the job its newest message is
   * about, unchecked, and on none when no message names one.
   */
  jobs?: readonly InboxJob[],
): Inbox {
  const resolved = resolveContacts(messages, contactOfJob);
  const known = knownAddresses(messages, addressesOfContact);
  const byCustomer = new Map<string, InboxMessage[]>();
  for (const message of resolved) {
    const to = message.contact_id ? null : recipients(message);
    const key = to
      ? addressedKey(message, to, known, contactOfJob)
      : inboxCustomerKey(message, known.threadSender);
    const list = byCustomer.get(key);
    if (list) list.push(message);
    else byCustomer.set(key, [message]);
  }

  const waiting: InboxCustomer[] = [];
  const quiet: InboxCustomer[] = [];
  let replied = 0;
  for (const [key, all] of byCustomer) {
    const inbound = all.filter((m) => m.direction === "inbound");
    if (!inbound.length) continue;
    const lastReply = all
      .filter((m) => m.direction === "outbound")
      .reduce((latest, m) => (m.sent_at > latest ? m.sent_at : latest), "");
    const unanswered = inbound
      .filter((m) => m.sent_at > lastReply && !handled.has(m.id))
      .sort((a, b) => a.sent_at.localeCompare(b.sent_at));
    const oldest = unanswered[0];
    const newest = unanswered[unanswered.length - 1];
    if (!oldest || !newest) {
      replied += 1;
      continue;
    }
    const customer = describe(key, unanswered, oldest, newest, jobs);
    if (unanswered.every((m) => messagePriority(m.signals).group === "quiet"))
      quiet.push(customer);
    else waiting.push(customer);
  }

  waiting.sort(
    (a, b) =>
      Number(b.same_day) - Number(a.same_day) ||
      a.waiting_since.localeCompare(b.waiting_since) ||
      a.key.localeCompare(b.key),
  );
  quiet.sort(
    (a, b) =>
      b.latest.sent_at.localeCompare(a.latest.sent_at) ||
      a.key.localeCompare(b.key),
  );
  return { waiting, no_reply_needed: quiet, replied };
}

function describe(
  key: string,
  unanswered: InboxMessage[],
  oldest: InboxMessage,
  newest: InboxMessage,
  jobs: readonly InboxJob[] | undefined,
): InboxCustomer {
  const aboutJobId =
    [...unanswered].reverse().find((m) => m.job_id)?.job_id ?? null;
  const priorities = unanswered.map((m) => messagePriority(m.signals));
  const strongest = priorities.reduce<Priority | null>(
    (best, p) => (best && best.score >= p.score ? best : p),
    null,
  );
  const cleaned = messageAttachments(newest.text);
  return {
    key,
    contact_id: newest.contact_id,
    job_id: jobs
      ? conversationJob(aboutJobId, newest.contact_id, jobs)
      : aboutJobId,
    from: newest.from,
    channel: newest.channel,
    unanswered,
    waiting_since: oldest.sent_at,
    latest: {
      text: cleaned.text,
      subject: newest.subject,
      sent_at: newest.sent_at,
      attachments: cleaned.attachments,
    },
    same_day: priorities.some((p) => p.group === "urgent"),
    reason: strongest ? reasonOf(strongest) : null,
  };
}

const CHECK_REASON_TEXT = new Set<string>(Object.values(CHECK_REASONS));

/** The strongest check, said once; "needs attention today" is its own tag. */
function reasonOf(priority: Priority): string | null {
  if (priority.group === "quiet" || priority.group === "unsorted") return null;
  if (priority.group === "glance") return priority.reasons[0] ?? null;
  return priority.reasons.find((r) => CHECK_REASON_TEXT.has(r)) ?? null;
}
